import { Alert, Autosuggest, Heading, Loading, Modal, Text } from '@acin/ui-core';
import React, { useEffect, useState } from 'react';
import { useMapControls } from '../../MapControlsProvider';
import { useCreateInternalRegulationObligationRuleControlMutation } from '../graphql/mutations/createInternalRegulationObligationRuleControl.generated';
import { useDeleteInternalRegulationObligationRuleControlMutation } from '../graphql/mutations/deleteInternalRegulationObligationRuleControl.generated';
import { useInternalControlsQuery } from '../graphql/queries/internalControls.generated';
import {
  IInternalRegulationObligationRuleControlsQuery,
  useInternalRegulationObligationRuleControlsQuery,
} from '../graphql/queries/internalRegulationObligationRuleControls.generated';
import * as SC from './MapControls.styled';
import { MappingItem } from './MappingItem';

type ControlMappingEdge =
  IInternalRegulationObligationRuleControlsQuery['internalRegulationObligationRuleControls']['edges'][number];

export const ControlMappingsList: React.FC = () => {
  const { activeObligationRule, activeObligationRuleControlMapping, onUpdateActiveObligationRuleControlMapping } =
    useMapControls();

  const [localEdges, setLocalEdges] = useState<ControlMappingEdge[]>([]);

  const { data, loading, error } = useInternalRegulationObligationRuleControlsQuery({
    variables: {
      input: {
        filters: {
          groups: activeObligationRule
            ? [
                {
                  groupKey: 'OBLIGATION_RULE_CONTROL_REGULATION_OBLIGATION_RULE_ID',
                  filterValues: [{ value: activeObligationRule.id }],
                },
              ]
            : [],
        },
      },
    },
    skip: !activeObligationRule,
    onCompleted(data) {
      const activeMappingInData = data?.internalRegulationObligationRuleControls?.edges.find(
        ({ node }) => node.id === activeObligationRuleControlMapping?.id,
      );

      if (
        !activeMappingInData ||
        (!activeObligationRuleControlMapping && data?.internalRegulationObligationRuleControls?.edges.length > 0)
      ) {
        onUpdateActiveObligationRuleControlMapping(data?.internalRegulationObligationRuleControls.edges[0].node);
      }
    },
  });

  // Mirror the fetched controls locally so newly added controls appear immediately.
  useEffect(() => {
    setLocalEdges(data?.internalRegulationObligationRuleControls?.edges ?? []);
  }, [activeObligationRule?.id, data?.internalRegulationObligationRuleControls?.edges]);

  const { data: controlsData } = useInternalControlsQuery({ fetchPolicy: 'cache-first' });
  const [createControl] = useCreateInternalRegulationObligationRuleControlMutation();
  const [deleteControl, { loading: deleteLoading }] = useDeleteInternalRegulationObligationRuleControlMutation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedControlForDelete, setSelectedControlForDelete] = useState<{ id: string; name: string } | null>(null);

  const controlEdges = controlsData?.internalControls?.edges ?? [];
  const controlLabel = (node: { code: string; latestName?: string | null }) =>
    node.latestName ? `${node.code} ${node.latestName}` : node.code;
  const controlOptions = controlEdges.map(({ node }) => ({ label: controlLabel(node) }));

  const handleAddControl = async (controlId: string) => {
    if (!activeObligationRule) return;
    const { data: created } = await createControl({
      variables: {
        input: { regulationObligationRuleId: activeObligationRule.id, controlId },
      },
      errorPolicy: 'all',
    });
    const newMapping = created?.createInternalRegulationObligationRuleControl;
    if (newMapping) {
      setLocalEdges((prev) => [
        ...prev,
        {
          __typename: 'InternalRegulationObligationRuleControlEdge',
          node: newMapping,
          cursor: newMapping.id,
        },
      ]);
    }
  };

  const handleDeleteControl = (mappingId: string) => {
    const edge = localEdges.find(({ node }) => node.id === mappingId);
    if (edge) {
      const latestInstance = edge.node.control?.latestInstance;
      const name = latestInstance ? `${edge.node.control?.code} ${latestInstance.name}` : 'Control not found';
      setSelectedControlForDelete({ id: mappingId, name });
      setIsModalOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedControlForDelete) return;
    const { errors } = await deleteControl({
      variables: { input: { id: selectedControlForDelete.id } },
      errorPolicy: 'all',
    });
    if (errors?.length) return;
    setLocalEdges((prev) => prev.filter((edge) => edge.node.id !== selectedControlForDelete.id));
    setIsModalOpen(false);
    setSelectedControlForDelete(null);
  };

  const handleCancelDelete = () => {
    setIsModalOpen(false);
    setSelectedControlForDelete(null);
  };

  const count = localEdges.length;

  return (
    <SC.RightSubColumn>
      <SC.ColumnHeader>
        <Heading size={2}>{count} Controls</Heading>
      </SC.ColumnHeader>
      <SC.ContentSection>
        {loading ? (
          <Loading position="absolute" withOverlay />
        ) : error ? (
          <Alert status="error" title="Error" message="Failed to load controls" />
        ) : count > 0 ? (
          localEdges.map((edge) => {
            const mapping = edge.node;
            const latestInstance = mapping.control?.latestInstance;
            const name = latestInstance ? mapping.control?.code + ' ' + latestInstance?.name : 'Control not found';

            return (
              <div
                key={mapping.id}
                onClick={() => onUpdateActiveObligationRuleControlMapping(mapping)}
                style={{
                  cursor: 'pointer',
                  backgroundColor: activeObligationRuleControlMapping?.id === mapping.id ? '#f0f0f0' : 'transparent',
                  borderRadius: '4px',
                  marginBottom: '8px',
                }}
              >
                <MappingItem id={mapping.id} name={name} onDelete={handleDeleteControl} />
              </div>
            );
          })
        ) : (
          <SC.PlaceholderText>No controls for this rule</SC.PlaceholderText>
        )}
      </SC.ContentSection>
      {activeObligationRule && (
        <SC.InputSection>
          <Autosuggest
            placeholder="Add a control"
            onSelect={(item) => {
              const match = controlEdges.find(({ node }) => controlLabel(node) === item.label);
              if (match) handleAddControl(match.node.id);
            }}
            items={controlOptions}
          />
        </SC.InputSection>
      )}
      <Modal
        show={isModalOpen}
        title="Delete this Control?"
        footerButtonPrimary={{ label: 'Confirm', onClick: handleConfirmDelete, disabled: deleteLoading }}
        footerButtonSecondary={{ label: 'Cancel', onClick: handleCancelDelete }}
      >
        <Text>{`Are you sure you want to delete the following control from this regulation: ${selectedControlForDelete?.name}?`}</Text>
      </Modal>
    </SC.RightSubColumn>
  );
};
